import { authenticationService, ROLE_NAMES } from "~/services";

// Raw session check — does NOT reflect an active Role Switcher override. Only safe in
// non-component contexts that can't call `useCurrentRole()` (e.g. Redux thunk `condition`
// guards). Components must pass their `useCurrentRole().isKpiOnly` into the helpers below
// instead, so a masked operator sees the full KPI-only experience of the role they selected.
export function isPartnerKpiOnlyUser() {
  return authenticationService?.currentUserInfo?.roles?.includes(ROLE_NAMES.RAN_KPI_ONLY) ?? false;
}

export function shouldHidePartnerKpiField(field: string, isKpiOnlyUser: boolean) {
  if (!isKpiOnlyUser) return false;

  const hiddenFields = new Set([
    "siteLocation",
    "siteAttributes",
    "siteTac",
    "radioLocation",
    "radioLocationSyncMode",
    "radioClockPool",
    "radioHeight",
    "radioSas",
    "radioMobility",
    "radioAdvanced",
    "radioInstallation",
    "radioCellFrequency",
    // "radioGroupType",
    "radioGroupPhase",
    "radioGroupFloor",
    "radioGroupArea",
    "radioGroupLocationSyncMode",
    "radioGroupClockPool",
    "radioGroupMap",
    "radioGroupMobility",
    "radioGroupSas",
    "radioGroupFrequency",
    "radioGroupFloorPlan",
  ]);

  return hiddenFields.has(field);
}

// Whitelisted keys for KPI-only create/edit payloads. Whitelist (rather than
// blacklist) so new schema fields default to "not sent" and don't quietly leak
// hidden-section defaults to the backend. `id` and `docVersion` are mandatory
// on edit (PUT URL routing + optimistic concurrency control) and harmless on
// create (id is the client-generated uuid; docVersion is undefined and skipped).
const KPI_ONLY_RADIO_GROUP_ALLOWED_FIELDS = [
  "id",
  "docVersion",
  "name",
  "description",
  "radioSite",
  "zoneType",
] as const;

export function pickKpiOnlyRadioGroupPayload<T extends Record<string, any>>(rg: T, isKpiOnlyUser: boolean): T {
  if (!isKpiOnlyUser) return rg;
  const picked: Record<string, any> = {};
  for (const f of KPI_ONLY_RADIO_GROUP_ALLOWED_FIELDS) {
    if (rg[f] !== undefined) picked[f] = rg[f];
  }
  return picked as T;
}

const KPI_ONLY_SITE_ALLOWED_FIELDS = [
  "id",
  "docVersion",
  "name",
  "description",
  "timezone",
] as const;

export function pickKpiOnlySitePayload<T extends Record<string, any>>(site: T, isKpiOnlyUser: boolean): T {
  if (!isKpiOnlyUser) return site;
  const picked: Record<string, any> = {};
  for (const f of KPI_ONLY_SITE_ALLOWED_FIELDS) {
    if (site[f] !== undefined) picked[f] = site[f];
  }
  return picked as T;
}
